/**
 * AdminContractHealthCard — read-only severity aggregate.
 * Phase 2D P4 enhancement (2026-04-24)
 *
 * Reuses renewal-status logic client-side against the already-loaded
 * agents list (no new backend endpoint).
 *
 * Expected agent shape (from GET /admin/field-agents): each may carry
 * `contract` object { start_date, end_date, renewal_note } OR legacy
 * `contract_start_date` / `contract_end_date` flat fields.
 */
import React, { useMemo } from "react";
import { AlertTriangle, Clock, Info, Ban, CheckCircle2 } from "lucide-react";

function jmToday() {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Jamaica", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date());
  const y = parts.find(p => p.type === "year").value;
  const m = parts.find(p => p.type === "month").value;
  const d = parts.find(p => p.type === "day").value;
  return `${y}-${m}-${d}`;
}

function daysBetween(fromISO, toISO) {
  try {
    const a = new Date(`${fromISO}T00:00:00`);
    const b = new Date(`${toISO}T00:00:00`);
    return Math.round((b - a) / 86400000);
  } catch { return null; }
}

function severityFor(endDate) {
  if (!endDate) return "info";
  const d = daysBetween(jmToday(), endDate);
  if (d === null) return "none";
  if (d < 0) return "expired";
  if (d === 0) return "urgent";
  if (d <= 7) return "warning";
  if (d <= 30) return "info";
  return "none";
}

const TILES = [
  { key: "expired", label: "Expired", Icon: Ban, cls: "bg-slate-900 text-white" },
  { key: "urgent", label: "Urgent (≤0d)", Icon: AlertTriangle, cls: "bg-rose-50 text-rose-800 border-rose-200" },
  { key: "warning", label: "Warning (≤7d)", Icon: Clock, cls: "bg-amber-50 text-amber-800 border-amber-200" },
  { key: "info", label: "Info (≤30d)", Icon: Info, cls: "bg-sky-50 text-sky-800 border-sky-200" },
  { key: "none", label: "Healthy / none", Icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
];

export default function AdminContractHealthCard({ agents }) {
  const counts = useMemo(() => {
    const out = { expired: 0, urgent: 0, warning: 0, info: 0, none: 0 };
    for (const a of agents || []) {
      const end = a?.contract?.end_date || a?.contract_end_date;
      out[severityFor(end)] += 1;
    }
    return out;
  }, [agents]);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4" data-testid="admin-contract-health-card">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-slate-900">Contract Health (read-only)</div>
        <div className="text-xs text-slate-500">Jamaica-time · live-computed</div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {TILES.map(({ key, label, Icon, cls }) => (
          <div
            key={key}
            data-testid={`contract-health-${key}`}
            className={`border rounded-lg px-3 py-2 flex items-center gap-2 ${cls}`}
          >
            <Icon className="w-4 h-4" />
            <div className="flex-1">
              <div className="text-xs opacity-80">{label}</div>
              <div className="text-lg font-bold leading-tight" data-testid={`contract-health-${key}-count`}>{counts[key]}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
